import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList } from 'lucide-react';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent';
import { PageHeader } from '../components/templates/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { Card } from '../components/atoms/Card/Card';
import { Badge } from '../components/atoms/Badge';
import { Button } from '../components/atoms/Button/Button';
import { Checkbox } from '../components/atoms/Checkbox';
import { Select } from '../components/atoms/Select/Select';
import { Alert } from '../components/atoms/Alert/Alert';
import { CONTENT_REVIEWED } from '../content/reviewStatus';
import { EmptyState } from '../components/atoms/EmptyState';
import { Inline } from '../components/atoms/Inline';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { NumericParameterInput } from '../components/atoms/NumericParameterInput/NumericParameterInput';
import { Stack } from '../components/atoms/Stack/Stack';
import { Heading } from '../components/atoms/Typography/Heading';
import { Text } from '../components/atoms/Typography/Text';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { GuidedTimer, formatClock } from '../components/measurements/GuidedTimer/GuidedTimer';
import { buildGuidedPlan, stepSeconds, stepText, type GuidedTest } from '../components/measurements/guided/guidedPlan';
import { parameterLabel } from '../components/measurements/measurementHistoryUtils';
import { useMeasurementSessionData } from '../hooks/useMeasurementSessionDefaults';
import { useGuidedTimer } from '../hooks/useGuidedTimer';
import { useSpeech } from '../hooks/useSpeech';
import { useWakeLock } from '../hooks/useWakeLock';
import { useSpokenValue, type SpokenValueResult } from '../hooks/useSpokenValue';
import { useOptionalCapture } from '../components/capture/CaptureContext';
import { VoiceRecorderButton } from '../components/atoms/VoiceRecorderButton';
import { signalTimerDone } from '../lib/timerSignal';
import { listTestIntervals, testIntervalsKey } from '../services/testIntervalService';
import { saveTestKitSelection } from '../services/measurementService';
import { PARAMETER_OPTIONS } from '../types/measurement';
import { useTranslation } from '../i18n/useTranslation';
import './GuidedTestSessionPage.css';

const READ_ALOUD_KEY = 'reefguide-guided-read-aloud';
const KNOWN = new Map(PARAMETER_OPTIONS.map((p) => [p.id, p]));

function readAloudDefault(): boolean {
  try {
    return localStorage.getItem(READ_ALOUD_KEY) !== '0';
  } catch {
    return true;
  }
}

/** A value of the guided session, handed to the session form's summary on "Finish". */
export interface GuidedPrefillEntry {
  parameterId: string;
  value: string;
  testKitProfileId: string | null;
}

type TestStatus = 'done' | 'skipped' | 'current' | 'open';

/**
 * Guided test session (docs/voiceFeature.md, UC-16; wireframe "Guided Test
 * Session"): the due tests one after another, each kit's steps read aloud and
 * its waiting times timed, the screen kept on. Values are typed (Release 2 adds
 * speaking them). Nothing is saved here — "Finish" opens the regular session
 * summary with the values filled in, where "Save session" stores them.
 */
export function GuidedTestSessionPage() {
  const { t, i18n } = useTranslation();
  const { aquariumId = '' } = useParams<{ aquariumId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { sessionDefaults, testKitProfiles, isLoading, isError, refetch } = useMeasurementSessionData(aquariumId);
  const intervals = useQuery({
    queryKey: testIntervalsKey(aquariumId),
    queryFn: () => listTestIntervals(aquariumId),
    enabled: aquariumId !== '',
  });

  /** Kits picked in this session (parameter → profile id, null = no kit); saved with the session. */
  const [kitChoice, setKitChoice] = useState<Record<string, string | null>>({});
  const plan = useMemo<GuidedTest[]>(
    () =>
      sessionDefaults && testKitProfiles && !intervals.isLoading
        ? buildGuidedPlan(sessionDefaults, testKitProfiles, intervals.data ?? [], Date.now(), kitChoice)
        : [],
    [sessionDefaults, testKitProfiles, intervals.isLoading, intervals.data, kitChoice],
  );

  const [testIndex, setTestIndex] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);
  const [values, setValues] = useState<Record<string, string>>({});
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const [readAloud, setReadAloud] = useState(readAloudDefault);
  const [timerDone, setTimerDone] = useState(false);
  const [remembered, setRemembered] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const valueRef = useRef<HTMLInputElement>(null);
  const speech = useSpeech();
  const wakeLock = useWakeLock(plan.length > 0);
  const capture = useOptionalCapture();
  /** One id per guided session: speaking values is charged once for it (UC-15). */
  const sessionId = useRef(crypto.randomUUID());
  const [heard, setHeard] = useState<string | null>(null);
  const spokenRef = useRef<(r: SpokenValueResult) => void>(() => undefined);
  const spoken = useSpokenValue(aquariumId, sessionId.current, i18n.language.slice(0, 2), (r) => spokenRef.current(r));
  const canSpeak = capture?.available === true;

  const test = plan[testIndex];
  const step = test?.steps[stepIndex];
  const plannedSec = test ? stepSeconds(test, stepIndex) : undefined;
  const label = useCallback(
    (parameterId: string) => parameterLabel(t, parameterId, KNOWN.get(parameterId)?.name ?? parameterId),
    [t],
  );

  const say = useCallback(
    (text: string) => {
      setAnnouncement(text);
      if (readAloud) speech.speak(text, i18n.language);
    },
    [readAloud, speech, i18n.language],
  );

  const timer = useGuidedTimer(() => {
    setTimerDone(true);
    signalTimerDone();
    say(String(t('guided.timer.done')));
  });

  // Entering a step: read it, and start its timer automatically (a waiting step).
  // The latest render's callback runs, but only when the step itself changes.
  const enterStep = useRef<() => void>(() => undefined);
  enterStep.current = () => {
    if (!test || !step) return;
    setTimerDone(false);
    setRemembered(false);
    timer.reset();
    const seconds = stepSeconds(test, stepIndex);
    const intro = stepIndex === 0 ? `${label(test.parameterId)}. ` : '';
    say(`${intro}${stepText(t, step, seconds)}`);
    if (step.type === 'wait' && seconds) timer.start(seconds);
    if (step.type === 'read_value' || step.type === 'measure_meter') setTimeout(() => valueRef.current?.focus(), 0);
  };
  const stepKey = test ? `${testIndex}:${stepIndex}:${test.parameterId}:${test.profile?.id ?? ''}` : null;
  useEffect(() => {
    if (stepKey !== null) enterStep.current();
  }, [stepKey]);

  const toggleReadAloud = (value: boolean): void => {
    setReadAloud(value);
    if (!value) speech.cancel();
    try {
      localStorage.setItem(READ_ALOUD_KEY, value ? '1' : '0');
    } catch {
      // Private mode: lasts for this visit.
    }
  };

  const goToTest = (index: number): void => {
    speech.cancel();
    setTestIndex(index);
    setStepIndex(0);
  };

  const nextTest = (): void => {
    const next = plan.findIndex((p, i) => i > testIndex && !values[p.parameterId] && !skipped.has(p.parameterId));
    if (next >= 0) goToTest(next);
    else finish();
  };

  // Another kit has other steps: start this test over with them.
  const chooseKit = (profileId: string): void => {
    if (!test) return;
    speech.cancel();
    setKitChoice((all) => ({ ...all, [test.parameterId]: profileId === '' ? null : profileId }));
    setStepIndex(0);
  };

  const skipTest = (): void => {
    if (!test) return;
    setSkipped((s) => new Set(s).add(test.parameterId));
    nextTest();
  };

  // A recognised utterance: a number fills the value ("Heard: 2.5 ppm"), a command acts.
  spokenRef.current = (result: SpokenValueResult): void => {
    if (!test) return;
    if (result.command === 'repeat') {
      if (step) speech.speak(stepText(t, step, plannedSec), i18n.language);
      return;
    }
    if (result.command === 'skip') {
      skipTest();
      return;
    }
    if (result.numbers.length > 0) {
      const value = String(result.numbers[0]);
      setValues((all) => ({ ...all, [test.parameterId]: value }));
      setHeard(value);
      setAnnouncement(String(t('guided.heard', { value, unit: test.profile?.unit ?? '' })));
    }
    if (result.command === 'next') {
      if (stepIndex < test.steps.length - 1) setStepIndex((s) => s + 1);
      else if ((result.numbers.length > 0 || (values[test.parameterId] ?? '').trim() !== '')) nextTest();
    } else if (result.numbers.length === 0) {
      setAnnouncement(String(t('guided.notUnderstood')));
    }
  };

  const finish = (): void => {
    speech.cancel();
    const entries: GuidedPrefillEntry[] = plan
      .filter((p) => (values[p.parameterId] ?? '').trim() !== '')
      .map((p) => ({ parameterId: p.parameterId, value: values[p.parameterId]!.trim(), testKitProfileId: p.profile?.id ?? null }));
    navigate(`/aquariums/${encodeURIComponent(aquariumId)}/measurements/session`, {
      state: { prefill: entries, returnTo: `/aquariums/${encodeURIComponent(aquariumId)}` },
    });
  };

  const rememberTime = async (): Promise<void> => {
    if (!test?.profile) return;
    const overrides = { ...test.waitOverrides, [String(stepIndex)]: timer.totalSec };
    await saveTestKitSelection(aquariumId, test.parameterId, test.profile.id, overrides);
    setRemembered(true);
    void queryClient.invalidateQueries({ queryKey: ['measurementSessionDefaults', aquariumId] });
  };

  const statusOf = (p: GuidedTest, i: number): TestStatus => {
    if (i === testIndex) return 'current';
    if ((values[p.parameterId] ?? '').trim() !== '') return 'done';
    if (skipped.has(p.parameterId)) return 'skipped';
    return 'open';
  };

  const value = test ? (values[test.parameterId] ?? '') : '';
  const numeric = Number(value.replace(',', '.'));
  const implausible =
    test?.profile && value.trim() !== '' && Number.isFinite(numeric)
      ? numeric < test.profile.plausibleMin || numeric > test.profile.plausibleMax
      : false;
  const lastStep = test ? stepIndex >= test.steps.length - 1 : false;
  const doneCount = plan.filter((p) => (values[p.parameterId] ?? '').trim() !== '').length;

  let body;
  if (isLoading || intervals.isLoading) {
    body = (
      <LiveRegion busy>
        <Text>{t('guided.loading')}</Text>
      </LiveRegion>
    );
  } else if (isError) {
    body = <LoadErrorState title={t('guided.error.title')} onRetry={() => void refetch()} retryLabel={t('common.retry')} />;
  } else if (!test || !step) {
    body = (
      <EmptyState
        icon={<ClipboardList size={32} aria-hidden="true" />}
        headline={String(t('guided.empty.title'))}
        description={String(t('guided.empty.body'))}
        actionLabel={String(t('guided.empty.setup'))}
        onAction={() => navigate(`/aquariums/${encodeURIComponent(aquariumId)}/measurements/session`)}
      />
    );
  } else {
    const unit = test.profile?.unit ?? KNOWN.get(test.parameterId)?.unit;
    body = (
      <Stack gap="8">
        <Card padding="lg">
          <Stack gap="4">
            {timerDone && (
              <Alert
                tone="info"
                live="assertive"
                compact
                title={t('guided.timer.doneTitle')}
                action={
                  <Button variant="primary" size="sm" onClick={() => (lastStep ? valueRef.current?.focus() : setStepIndex((s) => s + 1))}>
                    {lastStep ? t('guided.timer.enterValue') : t('guided.next')}
                  </Button>
                }
              >
                {t('guided.timer.done')}
              </Alert>
            )}
            <Inline gap="2">
              <Badge tone="info">{t('guided.stepBadge', { step: stepIndex + 1, total: test.steps.length })}</Badge>
              {test.due === 'overdue' && <Badge tone="warning">{t('guided.status.overdue')}</Badge>}
            </Inline>
            <Heading level={2}>{label(test.parameterId)}</Heading>
            {test.kits.length > 0 ? (
              <Select
                id="guided-kit"
                label={String(t('guided.kitLabel'))}
                value={test.profile?.id ?? ''}
                onChange={(e) => chooseKit(e.target.value)}
                options={[
                  { value: '', label: String(t('guided.kitNone')) },
                  ...test.kits.map((k) => ({ value: k.id, label: String(t('testSession.field.profileOption', { brand: k.brand, unit: k.unit })) })),
                ]}
                hint={test.profile ? undefined : String(t('guided.noKit'))}
                fullWidth
              />
            ) : (
              <Text color="secondary">{t('guided.noKit')}</Text>
            )}
            {test.profile?.procedure && !test.verified && (
              <Alert tone="info" live="off" compact>
                {t('guided.unverified')}
              </Alert>
            )}
            <ol className="guided-page__steps">
              {test.steps.map((s, i) => (
                <li
                  key={i}
                  aria-current={i === stepIndex ? 'step' : undefined}
                  className={`guided-page__step${i === stepIndex ? ' guided-page__step--current' : ''}${i < stepIndex ? ' guided-page__step--done' : ''}`}
                >
                  <Text as="span" size={i === stepIndex ? 'lg' : 'md'}>
                    {stepText(t, s, stepSeconds(test, i))}
                  </Text>
                </li>
              ))}
            </ol>
            {plannedSec !== undefined && (
              <>
                <GuidedTimer timer={timer} plannedSec={plannedSec} onStart={() => timer.start(plannedSec)} />
                {timer.status === 'done' && test.profile && timer.totalSec !== plannedSec && timer.totalSec > 0 && (
                  <Inline gap="2">
                    {remembered ? (
                      <Text size="sm" color="secondary">{t('guided.timer.remembered')}</Text>
                    ) : (
                      <Button variant="ghost" size="sm" onClick={() => void rememberTime()}>
                        {t('guided.timer.remember', { time: formatClock(timer.totalSec) })}
                      </Button>
                    )}
                  </Inline>
                )}
              </>
            )}
            <NumericParameterInput
              ref={valueRef}
              label={String(t('guided.valueLabel', { parameter: label(test.parameterId) }))}
              unit={unit}
              value={value}
              onChange={(v) => setValues((all) => ({ ...all, [test.parameterId]: v }))}
              error={implausible ? String(t('guided.valueImplausible')) : undefined}
              helperText={test.lastValue !== null ? String(t('guided.lastValue', { value: test.lastValue, unit: unit ?? '' })) : undefined}
            />
            {canSpeak && (
              <Stack gap="2">
                {spoken.status === 'listening' || spoken.status === 'connecting' ? (
                  <Button variant="primary" fullWidth onClick={spoken.stop} isLoading={spoken.status === 'connecting'}>
                    {t('guided.listening')}
                  </Button>
                ) : (
                  <VoiceRecorderButton
                    variant="secondary"
                    fullWidth
                    disabled={spoken.status === 'processing'}
                    onStart={() => {
                      speech.cancel();
                      setHeard(null);
                      void spoken.start();
                    }}
                  >
                    {t('guided.sayValue')}
                  </VoiceRecorderButton>
                )}
                {spoken.partial && (
                  <Text as="span" variant="caption" color="muted">
                    {spoken.partial}
                  </Text>
                )}
                {heard !== null && (
                  <Text size="sm" color="secondary">
                    {t('guided.heard', { value: heard, unit: unit ?? '' })}
                  </Text>
                )}
                {spoken.error && (
                  <Alert
                    tone="warning"
                    compact
                    action={
                      spoken.error === 'consent' ? (
                        <Button variant="secondary" size="sm" onClick={() => capture?.openConsent()}>
                          {t('guided.voiceConsent')}
                        </Button>
                      ) : undefined
                    }
                    onDismiss={spoken.reset}
                  >
                    {t(`guided.voiceError.${spoken.error}`)}
                  </Alert>
                )}
                <Text as="span" variant="caption" color="muted">
                  {t('guided.voiceHint', { count: capture?.settings?.pricing.guidedSessionCredits ?? 3 })}
                </Text>
              </Stack>
            )}
            <Inline gap="2" justify="end">
              {speech.supported && (
                <Button variant="ghost" onClick={() => speech.speak(stepText(t, step, plannedSec), i18n.language)}>
                  {t('guided.readAgain')}
                </Button>
              )}
              <Button variant="ghost" onClick={skipTest}>
                {t('guided.skipTest')}
              </Button>
              {!lastStep ? (
                <Button variant="primary" onClick={() => setStepIndex((s) => s + 1)}>
                  {t('guided.nextStep')}
                </Button>
              ) : (
                <Button variant="primary" onClick={nextTest} disabled={value.trim() === ''}>
                  {t('guided.next')}
                </Button>
              )}
            </Inline>
          </Stack>
        </Card>

        <Section title={t('guided.overviewTitle')}>
          <Card padding="lg">
            <Stack gap="4">
              <ul className="guided-page__overview">
                {plan.map((p, i) => {
                  const status = statusOf(p, i);
                  return (
                    <li key={p.parameterId} className="guided-page__overview-row">
                      <Button variant="ghost" size="sm" onClick={() => goToTest(i)} aria-current={status === 'current' ? 'step' : undefined}>
                        {label(p.parameterId)}
                      </Button>
                      <Text as="span" size="sm" color="secondary">
                        {status === 'done'
                          ? t('guided.status.doneValue', { value: values[p.parameterId] })
                          : t(`guided.status.${status}`)}
                      </Text>
                    </li>
                  );
                })}
              </ul>
              <Inline gap="4" justify="between">
                <Checkbox
                  label={t('guided.readAloud')}
                  checked={readAloud && speech.supported}
                  disabled={!speech.supported}
                  onChange={(e) => toggleReadAloud(e.target.checked)}
                />
                <Button variant="secondary" onClick={finish} disabled={doneCount === 0}>
                  {t('guided.finish', { count: doneCount })}
                </Button>
              </Inline>
              {wakeLock === 'unsupported' && (
                <Text variant="caption" color="muted">
                  {t('guided.wakeLockUnavailable')}
                </Text>
              )}
            </Stack>
          </Card>
        </Section>
      </Stack>
    );
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="narrow">
          <PageHeader
            title={t('guided.title')}
            description={plan.length > 0 ? t('guided.progress', { done: doneCount, total: plan.length }) : t('guided.subtitle')}
            actions={
              <Button variant="ghost" onClick={() => navigate(`/aquariums/${encodeURIComponent(aquariumId)}/measurements/session`)}>
                {t('guided.classic')}
              </Button>
            }
          />
          {!CONTENT_REVIEWED.testKitProcedures && (
            <Alert tone="warning" live="off" title={t('guided.reviewPendingTitle')} className="guided-page__review-pending" data-testid="review-pending">
              {t('guided.reviewPendingBody')}
            </Alert>
          )}
          {body}
          <LiveRegion visuallyHidden>{announcement}</LiveRegion>
        </PageContent>
      }
    />
  );
}

export default GuidedTestSessionPage;
